// src/utils/pdf.js
// ─────────────────────────────────────────────────────────────────────────────
// Browser-side PDF generation for quotations/invoices.
// Reuses the exact same HTML templates used for printing (quotationPrint.js /
// billPrint.js) so the WhatsApp PDF is pixel-identical to the printed copy.
// ─────────────────────────────────────────────────────────────────────────────

import { buildBillHtml } from "../pages/admin/print/billPrint";
import { buildQuotationHtml } from "../pages/admin/print/quotationPrint";

/**
 * Build the print HTML for a quotation/invoice.
 * Mirrors the logic in printQuotationDocument() so both print and PDF
 * use identical markup.
 */
export const buildQuotationPrintHtml = ({ quotation, customers, designs, branding, showPackagingCharges = true }) => {
  const customer = customers.find(c => c.id === quotation.customer_id) || {};
  const items = (quotation.items || []).map((item, index) => {
    const design = designs.find(d => d.id === item.design_id);
    const detail = item.design_no || design?.designNo || "—";
    const quantity = Math.max(1, Number(item.quantity) || 1);
    const price = Number(item.price || 0);
    const total = Number(item.subtotal || (price * quantity));
    return {
      serial: item.serial || index + 1,
      detail,
      quantity,
      price,
      total,
    };
  });
  const totalQuantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const assetBaseUrl = window.location.origin;
  const hasBillNumber = Boolean(String(quotation.bill_number || "").trim());

  return hasBillNumber
    ? buildBillHtml({ quotation, customer, branding, items, assetBaseUrl, showPackagingCharges })
    : buildQuotationHtml({ quotation, customer, branding, items, totalQuantity, assetBaseUrl, showPackagingCharges });
};

/**
 * Render a print document inside a hidden iframe and return a PDF Blob.
 *
 * Why an iframe?  Html2pdf internally clones the target element into a
 * temporary document, but that clone commonly drops `<head>` styles,
 * producing a blank canvas.  By writing the whole HTML document (including
 * `<head>`) into a hidden iframe we keep the styles intact, wait for them
 * to load in a true document context, and then capture with html2canvas
 * + jsPDF directly.
 */
async function renderInIframe(html) {
  // 1. Create a hidden iframe and write the full document into it.
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.top = "-9999px";   // far off-screen — html2canvas-picking still works
  iframe.style.left = "0";
  iframe.style.width = "800px";
  iframe.style.height = "1200px";
  iframe.style.background = "#fff";
  iframe.style.border = "none";
  iframe.style.zIndex = "-1";
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
  iframeDoc.open();
  iframeDoc.write(html);
  iframeDoc.close();

  // 2. Wait for stylesheets inside the iframe to load.
  const styleLinks = Array.from(iframeDoc.querySelectorAll('link[rel="stylesheet"]'));
  await Promise.all(
    styleLinks.map(
      (link) =>
        new Promise((resolve) => {
          if (link.sheet) return resolve();
          link.onload = () => resolve();
          link.onerror = () => resolve();
        })
    )
  );

  // 3. Wait for fonts inside the iframe.
  if (iframeDoc.fonts?.ready) {
    await iframeDoc.fonts.ready;
  }

  // 4. Wait for images inside the iframe.
  const images = iframeDoc.querySelectorAll("img");
  await Promise.all(
    Array.from(images).map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });
    })
  );

  return iframe;
}

/**
 * Generate a PDF Blob from a quotation/invoice using the exact print HTML.
 * Renders inside a hidden iframe for reliable style/image/font loading,
 * then captures with html2canvas + jsPDF.
 * @param {Object} params - { quotation, customers, designs, branding }
 * @returns {Promise<Blob>} The generated PDF as a Blob.
 */
export const generateQuotationPdf = async ({ quotation, customers, designs, branding, showPackagingCharges = true }) => {
  const html = buildQuotationPrintHtml({ quotation, customers, designs, branding, showPackagingCharges });

  let iframe = null;
  try {
    // Dynamically import html2canvas and jspdf (they are already installed
    // as transitive dependencies of html2pdf).
    const html2canvas = (await import("html2canvas")).default;
    const { jsPDF } = await import("jspdf");

    iframe = await renderInIframe(html);
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    const bodyEl = iframeDoc.body;

    // Capture the body with html2canvas — use the iframe's own window for
    // correct scroll dimensions.
    const canvas = await html2canvas(bodyEl, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      width: bodyEl.scrollWidth,
      height: bodyEl.scrollHeight,
      windowWidth: bodyEl.scrollWidth,
      windowHeight: bodyEl.scrollHeight,
    });

    // Build the PDF from the canvas image.
    const pdf = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.98);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    // Split into multiple pages if the canvas is taller than a single page.
    const pageHeight = pdf.internal.pageSize.getHeight();
    let remaining = pdfHeight;
    let srcY = 0;
    let page = 0;

    while (remaining > 0) {
      if (page > 0) pdf.addPage();
      const sliceH = Math.min(remaining, pageHeight);
      // Crop the canvas for the current page.
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = (sliceH * canvas.width) / pdfWidth;
      const ctx = pageCanvas.getContext("2d");
      ctx.drawImage(canvas, 0, srcY, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height);
      const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.98);
      pdf.addImage(pageImgData, "JPEG", 0, 0, pdfWidth, sliceH);
      srcY += pageCanvas.height;
      remaining -= pageHeight;
      page++;
    }

    return pdf.output("blob");
  } finally {
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }
};

/**
 * Convert a PDF Blob to a base64 data URL string (without the data: prefix).
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // Strip the "data:application/pdf;base64," prefix
      const base64 = typeof result === "string" ? result.split(",")[1] || "" : "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });